<template>
  <header>
    <a-form class="form" layout="vertical" ref="formRef" :model="formState">
      <a-form-item v-if="user?.customer?.type_business == 1" label="Empresa" name="entity" required>
        <entity-picker
          :showAll="false"
          defaultBehavior
          :value="formState.entity"
          @update:value="formState.entity = $event"
        />
      </a-form-item>

      <a-form-item label="Campaña" name="portfolio" required>
        <campaign-picker
          :showAll="false"
          :value="formState.portfolio"
          @update:value="formState.portfolio = $event"
          :entity="formState.entity"
        />
      </a-form-item>

      <a-form-item v-if="user?.customer?.enable_bases" label="Base" name="base" required>
        <bases-picker
          defaultBehavior
          :value="formState.base"
          @update:value="formState.base = $event"
          :entity="formState.entity"
          :portfolio="formState.portfolio"
        />
      </a-form-item>

      <a-form-item name="date" required>
        <template #label>
          Fecha

          <a-tooltip
            title="Fecha de la promesa de pago con sus tipificaciones realizadas el mismo día"
            placement="right"
          >
            <InfoCircleOutlined style="margin-left: 5px" />
          </a-tooltip>
        </template>

        <a-date-picker
          v-model:value="formState.date"
          format="DD/MM/YYYY"
          valueFormat="YYYY-MM-DD"
          placeholder="-- / -- / ----"
        />
      </a-form-item>
    </a-form>

    <div class="actions">
      <span v-if="totalRecords !== null" class="total">{{ totalRecords }} Registros</span>

      <a-tooltip placement="top">
        <template #title>
          <span v-if="isGlobalDownloading">Tiene una descarga pendiente</span>
          <span v-else>
            Se descargarán únicamente los datos de la tabla de acuerdo a los filtros seleccionados.
          </span>
        </template>

        <a-button
          type="link"
          :disabled="isButtonDisabled || isGlobalDownloading"
          :loading="isDownloading"
          @click="handleDownload"
        >
          Descargar
          <DownloadOutlined />
        </a-button>
      </a-tooltip>

      <a-button type="primary" :disabled="isButtonDisabled" @click="handleSearch">
        Buscar
        <SearchOutlined />
      </a-button>
    </div>
  </header>

  <a-table
    size="middle"
    id="inbound-table"
    :scroll="scrollStyle"
    :columns="progressColumns"
    :data-source="reportData"
    :loading="isTableLoading"
    :pagination="pagination"
    @change="handleTableChange"
  />
</template>

<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import { useStore } from "vuex";
import { DownloadOutlined, SearchOutlined, InfoCircleOutlined } from "@ant-design/icons-vue";

import type { Form, Report } from "@/@types/reports/payPromise/progress";
import type { UserData } from "@/@types/global";
import { useScrollResize } from "@/app/shared/composables/useScrollResize";
import { TABLE_PAGE_SIZE } from "@/app/shared/utils/constants";
import { fetchProgress, downloadProgress } from "@/app/reports/services";
import { progressColumns } from "@/app/reports/utils/tableColumns/payPromise/progressColumns";

import EntityPicker from "@/app/shared/components/pickers/EntityPicker.vue";
import CampaignPicker from "@/app/shared/components/pickers/CampaignPicker.vue";
import BasesPicker from "@/app/shared/components/pickers/BasesPicker.vue";
import { useGlobalDownloadState } from "@/app/shared/composables/useGlobalDownloadState";

const store = useStore();

const user = computed<UserData>(() => store.getters["getDataUser"]);
const { scrollStyle } = useScrollResize({ headerMargin: 420 });

// Form
const formState = reactive<Form>({
  portfolio: null,
  date: null,
});
const totalRecords = ref<number | null>(null);
const isButtonDisabled = computed(() => {
  for (const key in formState) {
    if (formState[key as keyof Form] === null) return true;
  }

  return false;
});
const isDownloading = ref(false);
const { isGlobalDownloading } = useGlobalDownloadState();

// Table
const reportData = ref<Report>([]);
const currentTablePage = ref(1);
const isTableLoading = ref(false);

const pagination = computed(() => ({
  total: reportData.value?.length,
  current: currentTablePage.value,
  pageSize: TABLE_PAGE_SIZE,
  showSizeChanger: false,
}));

const handleTableChange = async (page: { pageSize: number; current: number }) => {
  currentTablePage.value = page.current;
  // await fetchReportData();
};

const fetchReportData = async () => {
  isTableLoading.value = true;

  const response = await fetchProgress({ filters: formState });
  reportData.value = response;
  totalRecords.value = reportData.value?.length;

  isTableLoading.value = false;
};

const handleSearch = async () => {
  currentTablePage.value = 1;
  await fetchReportData();
};

const handleDownload = async () => {
  isDownloading.value = true;
  await downloadProgress({ filters: formState });
  isDownloading.value = false;
};
</script>

<style lang="sass" scoped>
header
  display: flex
  justify-content: space-between
  align-items: flex-end
  margin: 15px 0

.form
  display: flex
  gap: 10px

.ant-form-item
  margin: 0
  width: 180px

:deep(.ant-form-item-label)
  padding: 0

.actions
  display: flex
  align-items: center
  gap: 10px
</style>
